import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Navigate } from 'react-router-dom';
import {
  Box,
  Card,
  Container,
  TextField,
  InputAdornment,
  Typography,
  Grid,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  Select,
  MenuItem,
  Chip,
} from '@mui/material';
import ResponsiveList from '../../components/common/ResponsiveList';
import Label from '../../components/Label';
import SearchIcon from '@mui/icons-material/Search';
import FilterListIcon from '@mui/icons-material/FilterList';
import { customerApi } from '../../api/customerApi';
import { toast } from 'react-hot-toast';
import useAuth from '../../hooks/useAuth';
import { formatDateToMMDDYY } from '../../utils/helpers';
import SurveyDetailsModal from '../../components/dashboard/surveys/SurveyDetailsModal';

const SURVEY_TYPES = [
  { value: 'naltrexoneGroupSurvey', label: 'Naltrexone Group' },
  { value: 'ketamineGroupPreparationSurvey', label: 'Ketamine Prep' },
  { value: 'ketamineGroupIntegrationSurvey', label: 'Ketamine Integration' },
];

const calculateSentiment = (formData) => {
  if (!formData) return { score: 0, total: 0 };
  
  let sum = 0;
  let count = 0;

  Object.values(formData).forEach(({ value }) => {
    const numValue = parseInt(value);
    if (!isNaN(numValue)) {
      sum += numValue;
      count++;
    }
  });

  return {
    score: count > 0 ? (sum / count).toFixed(1) : 0,
    total: count
  };
};

const SupportGroupSurveyList = () => {
  const { user } = useAuth();
  const roles = user?.roles || [];
  const hasAccess = roles.some(role => ['admin', 'ops', 'guide', 'success_manager', 'sales'].includes(role));

  if (!hasAccess) {
    return <Navigate to="/dashboard" />;
  }

  const [surveys, setSurveys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedSurvey, setSelectedSurvey] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [openFilters, setOpenFilters] = useState(false);
  const [filters, setFilters] = useState(() => {
    const savedFilters = sessionStorage.getItem('supportGroupSurveyFilters');
    return savedFilters ? JSON.parse(savedFilters) : {
      surveyType: '',
    };
  });
  const [paginationModel, setPaginationModel] = useState({
    pageSize: 50,
    page: 0,
  });

  const fetchSurveys = async () => {
    try {
      const response = await customerApi.getSupportGroupSurveys();
      setSurveys((response || []).map(survey => {
        const sentiment = calculateSentiment(survey.form_data);
        return {
          ...survey,
          id: survey.id,
          formType: survey.form_name,
          submittedAt: formatDateToMMDDYY(survey.created_at),
          sentiment: sentiment.score,
          totalQuestions: sentiment.total,
          emailOptional: survey.form_data?.emailOptional?.value || 'N/A',
        };
      }));
    } catch (error) {
      if (error.response?.status !== 401) {
        console.error('Failed to fetch surveys:', error);
        toast.error('Failed to load surveys');
        setSurveys([]);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSurveys();
  }, []);

  const handleViewDetails = (survey) => {
    setSelectedSurvey(survey);
    setIsModalOpen(true);
  };

  const handleOpenFilters = () => {
    setOpenFilters(true);
  };

  const handleCloseFilters = () => {
    setOpenFilters(false);
  };

  const handleFilterChange = (filterType, value) => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      [filterType]: value
    }));
  };

  const handleApplyFilters = () => {
    sessionStorage.setItem('supportGroupSurveyFilters', JSON.stringify(filters));
    setPaginationModel((prevModel) => ({ ...prevModel, page: 0 }));
    handleCloseFilters();
  };

  const handleRemoveFilter = (filterType) => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      [filterType]: ''
    }));
    const newFilters = {
      ...filters,
      [filterType]: ''
    };
    sessionStorage.setItem('supportGroupSurveyFilters', JSON.stringify(newFilters));
  };

  const handleRemoveAllFilters = () => {
    const defaultFilters = {
      surveyType: '',
    };
    setFilters(defaultFilters);
    setSearchTerm('');
    sessionStorage.setItem('supportGroupSurveyFilters', JSON.stringify(defaultFilters));
    sessionStorage.setItem('supportGroupSurveyQuery', '');
  };

  const renderActiveFilters = () => {
    const activeFilters = Object.entries(filters).filter(([, value]) => value !== '');
    
    if (activeFilters.length === 0 && !searchTerm) return null;

    return (
      <Box sx={{ mt: 2, mb: 2 }}>
        {activeFilters.map(([key, value]) => {
          let displayValue = value;
          if (key === 'surveyType') {
            const surveyType = SURVEY_TYPES.find(type => type.value === value);
            displayValue = surveyType ? surveyType.label : value;
          }
          return (
            <Chip
              key={key}
              label={`${key === 'surveyType' ? 'Type' : key}: ${displayValue}`}
              onDelete={() => handleRemoveFilter(key)}
              color="primary"
              variant="outlined"
              sx={{ mr: 1, mb: 1 }}
            />
          );
        })}
        {searchTerm && (
          <Chip
            label={`Search: ${searchTerm}`}
            onDelete={() => setSearchTerm('')}
            color="primary"
            variant="outlined"
            sx={{ mr: 1, mb: 1 }}
          />
        )}
        {(activeFilters.length > 0 || searchTerm) && (
          <Chip
            label="Clear All Filters"
            onDelete={handleRemoveAllFilters}
            color="secondary"
            variant="outlined"
            sx={{ mr: 1, mb: 1 }}
          />
        )}
      </Box>
    );
  };

  const columns = [
    {
      field: 'email',
      headerName: 'Email',
      width: 250,
      renderCell: (params) => params.value || params.row.emailOptional || 'Anonymous',
    },
    {
      field: 'formType',
      headerName: 'Survey Type',
      width: 200,
      valueGetter: (params) => {
        switch (params.value) {
          case 'naltrexoneGroupSurvey':
            return 'Naltrexone Group';
          case 'ketamineGroupPreparationSurvey':
            return 'Ketamine Prep';
          case 'ketamineGroupIntegrationSurvey':
            return 'Ketamine Integration';
          default:
            return params.value;
        }
      }
    },
    {
      field: 'submittedAt',
      headerName: 'Submitted',
      width: 120,
    },
    {
      field: 'sentiment',
      headerName: 'Overall Score',
      width: 150,
      renderCell: (params) => {
        const score = parseFloat(params.value);
        let color = 'inherit';
        if (score >= 4) color = 'success.main';
        else if (score >= 3) color = 'warning.main';
        else if (score > 0) color = 'error.main';

        return (
          <Typography color={color} variant="subtitle2">
            {score > 0 ? `${score}/5` : 'N/A'}
          </Typography>
        );
      },
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 120,
      renderCell: (params) => (
        <Button
          variant="outlined"
          size="small"
          onClick={() => handleViewDetails(params.row)}
        >
          View Details
        </Button>
      ),
    },
  ];

  const filteredSurveys = surveys.filter((survey) => {
    const searchLower = searchTerm.toLowerCase();
    const matchesSearch = (survey.email || '').toLowerCase().includes(searchLower) ||
                         survey.formType.toLowerCase().includes(searchLower);
    const matchesType = !filters.surveyType || survey.formType === filters.surveyType;
    
    return matchesSearch && matchesType;
  });

  return (
    <>
      <Helmet>
        <title>Dashboard: Support Group Surveys | Choose Life</title>
      </Helmet>
      <Box
        sx={{
          backgroundColor: 'background.default',
          minHeight: '100%',
          py: 3
        }}
      >
        <Container maxWidth={false}>
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12}>
              <Typography variant="h4">
                Support Group Surveys
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  )
                }}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search by email or survey type"
                variant="outlined"
                value={searchTerm}
              />
            </Grid>
            <Grid item xs={12} md={6} sx={{ display: 'flex', justifyContent: 'flex-start' }}>
              <Button
                variant="contained"
                color="primary"
                startIcon={<FilterListIcon />}
                onClick={handleOpenFilters}
                size="small"
              >
                Filters
              </Button>
            </Grid>
          </Grid>

          {renderActiveFilters()}

          <Box sx={{ mt: 3 }}>
            <Card>
              <ResponsiveList
                rows={filteredSurveys}
                columns={columns}
                loading={loading}
                pageSizeOptions={[25, 50, 100]}
                paginationModel={paginationModel}
                paginationMode="client"
                onPaginationModelChange={setPaginationModel}
                desktopHeight="calc(100vh - 300px)"
                renderMobileCard={(row) => (
                  <Card sx={{ p: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <Typography variant="subtitle2">
                        {row.email || row.emailOptional || 'Anonymous'}
                      </Typography>
                      <Chip label={row.formType} size="small" variant="outlined" />
                    </Box>
                    <Box sx={{ mt: 0.5 }}>
                      <Label color={(() => {
                        const s = row.sentiment || 0;
                        if (s >= 0.7) return 'success';
                        if (s >= 0.4) return 'warning';
                        return 'error';
                      })()}
                      >
                        {`${Math.round((row.sentiment || 0) * 100)}%`}
                      </Label>
                    </Box>
                    <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5 }}>
                      {row.submittedAt}
                    </Typography>
                    <Box sx={{ mt: 1 }}>
                      <Button size="small" variant="outlined" onClick={() => handleViewDetails(row)}>
                        View Details
                      </Button>
                    </Box>
                  </Card>
                )}
              />
            </Card>
          </Box>
        </Container>
      </Box>

      <Dialog open={openFilters} onClose={handleCloseFilters} maxWidth="md" fullWidth>
        <DialogTitle>Filters</DialogTitle>
        <DialogContent>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <Typography variant="subtitle1">Survey Type</Typography>
                <Select
                  value={filters.surveyType}
                  onChange={(e) => handleFilterChange('surveyType', e.target.value)}
                >
                  <MenuItem value="">All Types</MenuItem>
                  {SURVEY_TYPES.map((type) => (
                    <MenuItem key={type.value} value={type.value}>
                      {type.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseFilters} color="primary">
            Cancel
          </Button>
          <Button onClick={handleApplyFilters} color="primary" variant="contained">
            Apply Filters
          </Button>
        </DialogActions>
      </Dialog>

      <SurveyDetailsModal
        open={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setSelectedSurvey(null);
        }}
        survey={selectedSurvey}
      />
    </>
  );
};

export default SupportGroupSurveyList; 